import React, { useState, useMemo } from "react";
import { Breadcrumb } from "../../components/ui";
import { useParams, useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Panel } from "primereact/panel";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import { InputIcon } from "primereact/inputicon";
import toast from "react-hot-toast";

import {
  useGetWorkshopByIdQuery,
  useGetWorkshopEnrollmentsQuery,
  useGetWorkshopPaymentsQuery,
  useGetWorkshopAlertsQuery,
  useMarkAttendanceMutation,
  useMarkAbsenceMutation,
  useManualDropMutation,
  useRecordOfflinePaymentMutation,
} from "../../store/services/workshops";
import { useGetTenantsQuery } from "../../store/services/tenants";
import {
  getWorkshopStatusLabel,
  getWorkshopStatusSeverity,
  getEnrollmentStatusLabel,
  getEnrollmentStatusSeverity,
  getPaymentStatusLabel,
  getPaymentSourceLabel,
} from "../../utils/constants/workshops-constants";
import { EnrollmentWithUser, WorkshopPayment } from "../../models/workshops";

export const WorkshopDetail = () => {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const id = workshopId || "";

  const { data: workshop, isLoading } = useGetWorkshopByIdQuery(id);
  const { data: enrollments, isLoading: loadingEnrollments } =
    useGetWorkshopEnrollmentsQuery(id);
  const { data: payments, isLoading: loadingPayments } =
    useGetWorkshopPaymentsQuery(id);
  const { data: alerts } = useGetWorkshopAlertsQuery();
  const { data: tenants } = useGetTenantsQuery({});

  const [markAttendance] = useMarkAttendanceMutation();
  const [markAbsence] = useMarkAbsenceMutation();
  const [manualDrop] = useManualDropMutation();
  const [recordOfflinePayment, { isLoading: isRecordingPayment }] =
    useRecordOfflinePaymentMutation();

  const [paymentDialog, setPaymentDialog] = useState<{
    visible: boolean;
    enrollmentId: string;
    userName: string;
  }>({ visible: false, enrollmentId: "", userName: "" });
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);

  const [dropDialog, setDropDialog] = useState<{
    visible: boolean;
    enrollmentId: string;
    userName: string;
  }>({ visible: false, enrollmentId: "", userName: "" });

  // Filters: Alumnos tab
  const [enrollmentFilter, setEnrollmentFilter] = useState("");

  // Filters: Pagos tab
  const [paymentEmailFilter, setPaymentEmailFilter] = useState("");
  const [paymentDateFrom, setPaymentDateFrom] = useState<Date | null>(null);
  const [paymentDateTo, setPaymentDateTo] = useState<Date | null>(null);

  const tenantName = React.useMemo(() => {
    if (!tenants || !workshop) return "";
    const t = (tenants as any[]).find(
      (t: any) => String(t.id) === String(workshop.tenantId)
    );
    return t?.provider || workshop.tenantId;
  }, [tenants, workshop]);

  const workshopAlerts = React.useMemo(() => {
    if (!alerts || !id) return [];
    return alerts.filter((a) => a.workshopId === id);
  }, [alerts, id]);

  const filteredEnrollments = useMemo(() => {
    if (!enrollments) return [];
    if (!enrollmentFilter.trim()) return enrollments;
    const q = enrollmentFilter.toLowerCase();
    return enrollments.filter((e) => {
      const fullName = `${e.userFirstName} ${e.userLastName}`.toLowerCase();
      return fullName.includes(q) || e.userEmail.toLowerCase().includes(q);
    });
  }, [enrollments, enrollmentFilter]);

  const filteredPayments = useMemo(() => {
    if (!payments) return [];
    let result = payments;
    if (paymentEmailFilter.trim()) {
      const q = paymentEmailFilter.toLowerCase();
      result = result.filter((p) => {
        const name = `${p.userFirstName || ""} ${p.userLastName || ""}`.toLowerCase();
        return (p.userEmail || "").toLowerCase().includes(q) || name.includes(q);
      });
    }
    if (paymentDateFrom) {
      const fromTs = Math.floor(paymentDateFrom.getTime() / 1000);
      result = result.filter((p) => p.createdAt >= fromTs);
    }
    if (paymentDateTo) {
      const toTs = Math.floor(paymentDateTo.getTime() / 1000) + 86400; // end of day
      result = result.filter((p) => p.createdAt < toTs);
    }
    return result;
  }, [payments, paymentEmailFilter, paymentDateFrom, paymentDateTo]);

  if (isLoading) {
    return (
      <div className="gap-4 flex flex-column justify-content-center align-items-center p-3 min-h-11rem p-8">
        <h1 className="text-center text-3xl font-bold mb-6">Cargando contenido</h1>
        <ProgressSpinner />
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="p-card p-5 text-center">
        <h2>Taller no encontrado</h2>
        <Button
          label="Volver"
          icon="pi pi-arrow-left"
          onClick={() => navigate("/workshops")}
        />
      </div>
    );
  }

  const handleAttendance = (enrollmentId: string) => {
    markAttendance({ workshopId: id, enrollmentId })
      .unwrap()
      .then(() => toast.success("Asistencia registrada"))
      .catch(() => toast.error("Error al registrar asistencia"));
  };

  const handleAbsence = (enrollmentId: string) => {
    markAbsence({ workshopId: id, enrollmentId })
      .unwrap()
      .then(() => toast.success("Inasistencia registrada"))
      .catch(() => toast.error("Error al registrar inasistencia"));
  };

  const handleDrop = () => {
    manualDrop({ workshopId: id, enrollmentId: dropDialog.enrollmentId })
      .unwrap()
      .then(() => {
        toast.success("Baja registrada");
        setDropDialog({ visible: false, enrollmentId: "", userName: "" });
      })
      .catch(() => toast.error("Error al dar de baja"));
  };

  const handleOfflinePayment = () => {
    if (!paymentAmount || paymentAmount <= 0) {
      toast.error("Ingrese un monto válido");
      return;
    }
    recordOfflinePayment({
      workshopId: id,
      enrollmentId: paymentDialog.enrollmentId,
      amount: paymentAmount,
    })
      .unwrap()
      .then(() => {
        toast.success("Pago registrado");
        setPaymentDialog({ visible: false, enrollmentId: "", userName: "" });
        setPaymentAmount(null);
      })
      .catch(() => toast.error("Error al registrar pago"));
  };

  const formatDate = (ts: number | null) => {
    if (!ts) return "—";
    return new Date(ts * 1000).toLocaleDateString("es-AR");
  };

  const formatDateTime = (ts: number | null) => {
    if (!ts) return "—";
    const d = new Date(ts * 1000);
    return `${d.toLocaleDateString("es-AR")} ${d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}`;
  };

  const enrollmentActionsTemplate = (row: EnrollmentWithUser) => {
    const isActive =
      row.status === "active" || row.status === "pending_payment";
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-check"
          rounded
          text
          severity="success"
          tooltip="Presente"
          tooltipOptions={{ position: "top" }}
          onClick={() => handleAttendance(row.id)}
          disabled={!isActive}
        />
        <Button
          icon="pi pi-times"
          rounded
          text
          severity="warning"
          tooltip="Ausente"
          tooltipOptions={{ position: "top" }}
          onClick={() => handleAbsence(row.id)}
          disabled={!isActive}
        />
        <Button
          icon="pi pi-dollar"
          rounded
          text
          severity="info"
          tooltip="Pago offline"
          tooltipOptions={{ position: "top" }}
          onClick={() =>
            setPaymentDialog({
              visible: true,
              enrollmentId: row.id,
              userName: `${row.userFirstName} ${row.userLastName}`,
            })
          }
          disabled={!isActive}
        />
        <Button
          icon="pi pi-user-minus"
          rounded
          text
          severity="danger"
          tooltip="Dar de baja"
          tooltipOptions={{ position: "top" }}
          onClick={() =>
            setDropDialog({
              visible: true,
              enrollmentId: row.id,
              userName: `${row.userFirstName} ${row.userLastName}`,
            })
          }
          disabled={!isActive}
        />
      </div>
    );
  };

  const paymentAmountTemplate = (row: WorkshopPayment) => (
    <span>${row.amount.toLocaleString("es-AR")}</span>
  );

  return (
    <>
      <Breadcrumb items={[{ label: "Talleres", to: "/workshops" }, { label: workshop?.name ?? "Detalle" }]} />
    <div className="p-card gap-4 p-3">
      {/* Header */}
      <div className="flex justify-content-between align-items-center mb-4">
        <div className="flex align-items-center gap-3">
          <Button
            icon="pi pi-arrow-left"
            rounded
            text
            onClick={() => navigate("/workshops")}
          />
          <h1 className="m-0">{workshop.name}</h1>
          <Tag
            value={getWorkshopStatusLabel(workshop.status)}
            severity={getWorkshopStatusSeverity(workshop.status)}
          />
        </div>
        <Button
          label="Editar"
          icon="pi pi-pencil"
          onClick={() => navigate(`/workshops/${workshop.id}/edit`)}
        />
      </div>

      <TabView>
        {/* ── Tab: Información ── */}
        <TabPanel header="Información">
          <Panel header="Datos Generales" className="mb-4" toggleable>
            <div className="grid">
              <div className="col-12 lg:col-6">
                <div className="text-color-secondary text-sm">Tipo de Actividad</div>
                <div className="font-medium text-lg">{workshop.activityType}</div>
              </div>
              <div className="col-12 lg:col-6">
                <div className="text-color-secondary text-sm">Tenant</div>
                <div className="font-medium text-lg">{tenantName}</div>
              </div>
              <div className="col-12">
                <div className="text-color-secondary text-sm">Descripción</div>
                <div className="font-medium">{workshop.description || "—"}</div>
              </div>
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Duración de la mensualidad</div>
                <div className="font-medium">{workshop.durationMonths === 1 ? "1 mes" : `${workshop.durationMonths} meses`}</div>
              </div>
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Capacidad</div>
                <div className="font-medium">
                  {workshop.activeEnrollments}/{workshop.maxCapacity}
                </div>
              </div>
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Instructor</div>
                <div className="font-medium">{workshop.instructorName || "—"}</div>
              </div>
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Ubicación</div>
                <div className="font-medium">{workshop.location || "—"}</div>
              </div>
            </div>
          </Panel>

          <Panel header="Fechas y Precio" className="mb-4" toggleable>
            <div className="grid">
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Tipo</div>
                <div className="font-medium">
                  {workshop.isRolling ? (
                    <Tag value="Rolling" severity="info" />
                  ) : (
                    "Fecha fija"
                  )}
                </div>
              </div>
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Fecha de inicio</div>
                <div className="font-medium">{formatDate(workshop.startDate)}</div>
              </div>
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Fecha de fin</div>
                <div className="font-medium">{formatDate(workshop.endDate)}</div>
              </div>
              {!workshop.isRolling && (
                <div className="col-6 lg:col-3">
                  <div className="text-color-secondary text-sm">Deadline Inscripción</div>
                  <div className="font-medium">
                    {formatDateTime(workshop.registrationDeadline)}
                  </div>
                </div>
              )}
              <div className="col-6 lg:col-3">
                <div className="text-color-secondary text-sm">Precio</div>
                <div className="font-medium">
                  {workshop.isPaid
                    ? `$${(workshop.monthlyPrice || 0).toLocaleString("es-AR")}/mes`
                    : "Gratuito"}
                </div>
              </div>
            </div>
          </Panel>
        </TabPanel>

        {/* ── Tab: Alumnos ── */}
        <TabPanel header={`Alumnos (${enrollments?.length || 0})`}>
          <div className="flex justify-content-end mb-3">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={enrollmentFilter}
                onChange={(e) => setEnrollmentFilter(e.target.value)}
                placeholder="Buscar por nombre o email"
                style={{ width: "300px" }}
              />
            </IconField>
          </div>
          <DataTable
            value={filteredEnrollments}
            loading={loadingEnrollments}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            emptyMessage="No hay inscriptos"
            stripedRows
            dataKey="id"
          >
            <Column
              header="Alumno"
              body={(row: EnrollmentWithUser) =>
                `${row.userFirstName} ${row.userLastName}`
              }
              sortable
              sortField="userLastName"
              style={{ minWidth: "12rem" }}
            />
            <Column field="userEmail" header="Email" style={{ minWidth: "14rem" }} />
            <Column
              header="Estado"
              body={(row: EnrollmentWithUser) => (
                <Tag
                  value={getEnrollmentStatusLabel(row.status)}
                  severity={getEnrollmentStatusSeverity(row.status)}
                />
              )}
              sortable
              sortField="status"
              style={{ minWidth: "10rem" }}
            />
            <Column
              field="consecutiveAbsences"
              header="Faltas"
              sortable
              style={{ minWidth: "6rem" }}
            />
            <Column
              header="Último Pago"
              body={(row: EnrollmentWithUser) => formatDate(row.lastPaymentDate)}
              style={{ minWidth: "8rem" }}
            />
            <Column
              header="Próx. Vencimiento"
              body={(row: EnrollmentWithUser) => formatDate(row.nextPaymentDue)}
              style={{ minWidth: "10rem" }}
            />
            <Column
              header="Acciones"
              body={enrollmentActionsTemplate}
              style={{ minWidth: "14rem" }}
            />
          </DataTable>
        </TabPanel>

        {/* ── Tab: Pagos ── */}
        <TabPanel header={`Pagos (${payments?.length || 0})`}>
          <div className="flex gap-3 mb-3 align-items-end flex-wrap">
            <div className="field mb-0">
              <label className="block text-sm mb-1">Buscar alumno</label>
              <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText
                  value={paymentEmailFilter}
                  onChange={(e) => setPaymentEmailFilter(e.target.value)}
                  placeholder="Nombre o email"
                  style={{ width: "250px" }}
                />
              </IconField>
            </div>
            <DateRangeFilter
              variant="stacked"
              dateFrom={paymentDateFrom}
              dateTo={paymentDateTo}
              onDateFromChange={setPaymentDateFrom}
              onDateToChange={setPaymentDateTo}
              startDateId="payment-date-from"
              endDateId="payment-date-to"
              labelFrom="Desde"
              labelTo="Hasta"
              labelClassName="block text-sm mb-1"
              showClearButtons={false}
              columnClassName="field mb-0"
              calendarStyle={{ width: "180px" }}
            />
            {(paymentEmailFilter || paymentDateFrom || paymentDateTo) && (
              <Button
                icon="pi pi-filter-slash"
                rounded
                text
                severity="secondary"
                tooltip="Limpiar filtros"
                tooltipOptions={{ position: "top" }}
                onClick={() => {
                  setPaymentEmailFilter("");
                  setPaymentDateFrom(null);
                  setPaymentDateTo(null);
                }}
              />
            )}
          </div>
          <DataTable
            value={filteredPayments}
            loading={loadingPayments}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            emptyMessage="No hay pagos registrados"
            stripedRows
            dataKey="id"
            sortField="createdAt"
            sortOrder={-1}
          >
            <Column
              header="Alumno"
              body={(row: WorkshopPayment) =>
                `${row.userFirstName || ""} ${row.userLastName || ""}`.trim() || "—"
              }
              style={{ minWidth: "12rem" }}
            />
            <Column
              header="Email"
              body={(row: WorkshopPayment) => row.userEmail || "—"}
              style={{ minWidth: "14rem" }}
            />
            <Column
              header="Fecha"
              body={(row: WorkshopPayment) => formatDate(row.createdAt)}
              sortable
              sortField="createdAt"
              style={{ minWidth: "8rem" }}
            />
            <Column
              header="Monto"
              body={paymentAmountTemplate}
              style={{ minWidth: "8rem" }}
            />
            <Column
              header="Estado"
              body={(row: WorkshopPayment) => (
                <Tag value={getPaymentStatusLabel(row.status)} />
              )}
              style={{ minWidth: "8rem" }}
            />
            <Column
              header="Fuente"
              body={(row: WorkshopPayment) => getPaymentSourceLabel(row.source)}
              style={{ minWidth: "8rem" }}
            />
            <Column field="paymentId" header="ID Pago" style={{ minWidth: "10rem" }} />
          </DataTable>
        </TabPanel>

        {/* ── Tab: Alertas ── */}
        <TabPanel
          header={
            <span>
              Alertas{" "}
              {workshopAlerts.length > 0 && (
                <Tag value={workshopAlerts.length} severity="danger" className="ml-2" />
              )}
            </span>
          }
        >
          {workshopAlerts.length === 0 ? (
            <div className="text-center p-5 text-color-secondary">
              <i className="pi pi-check-circle text-4xl mb-3" style={{ color: "var(--green-500)" }}></i>
              <p>No hay alertas activas para este taller.</p>
            </div>
          ) : (
            <DataTable
              value={workshopAlerts}
              emptyMessage="Sin alertas"
              stripedRows
            >
              <Column field="type" header="Tipo" body={(row) => (
                <Tag
                  value={row.type === "no_attendance" ? "Sin asistencia" : "Sin MP configurado"}
                  severity={row.type === "no_attendance" ? "warning" : "danger"}
                />
              )} />
              <Column field="userName" header="Usuario" />
              <Column field="message" header="Mensaje" />
            </DataTable>
          )}
        </TabPanel>
      </TabView>

      {/* ── Offline Payment Dialog ── */}
      <Dialog
        visible={paymentDialog.visible}
        style={{ width: "400px" }}
        header={`Registrar Pago - ${paymentDialog.userName}`}
        modal
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              outlined
              onClick={() => {
                setPaymentDialog({ visible: false, enrollmentId: "", userName: "" });
                setPaymentAmount(null);
              }}
            />
            <Button
              label="Registrar"
              icon="pi pi-check"
              onClick={handleOfflinePayment}
              loading={isRecordingPayment}
            />
          </div>
        }
        onHide={() => {
          setPaymentDialog({ visible: false, enrollmentId: "", userName: "" });
          setPaymentAmount(null);
        }}
      >
        <div className="field">
          <label htmlFor="paymentAmount">Monto ($)</label>
          <InputNumber
            id="paymentAmount"
            value={paymentAmount}
            onValueChange={(e) => setPaymentAmount(e.value ?? null)}
            mode="currency"
            currency="ARS"
            locale="es-AR"
            min={0}
            className="w-full"
          />
        </div>
      </Dialog>

      {/* ── Drop Confirmation Dialog ── */}
      <Dialog
        visible={dropDialog.visible}
        style={{ width: "450px" }}
        header="Confirmar baja"
        modal
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              outlined
              onClick={() =>
                setDropDialog({ visible: false, enrollmentId: "", userName: "" })
              }
            />
            <Button
              label="Dar de baja"
              icon="pi pi-user-minus"
              severity="danger"
              onClick={handleDrop}
            />
          </div>
        }
        onHide={() =>
          setDropDialog({ visible: false, enrollmentId: "", userName: "" })
        }
      >
        <div className="flex align-items-center gap-3">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "2rem" }}
          ></i>
          <span>
            ¿Está seguro que desea dar de baja a{" "}
            <strong>{dropDialog.userName}</strong>?
          </span>
        </div>
      </Dialog>
    </div>
    </>
  );
};
